import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Calendar, MessageCircle } from "lucide-react";
import { CLINIC } from "../lib/clinic";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  { value: "neurology", label: "Neurology Consultation" },
  { value: "psychology", label: "Psychology Consultation" },
  { value: "online", label: "Online Consultation" },
];

export const AppointmentForm = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service_type: "neurology",
    preferred_date: "",
    preferred_time: "",
    concern: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please enter your name and phone number");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, form);
      toast.success("Appointment request received! We'll confirm shortly on WhatsApp.");

      // Open WhatsApp with prefilled message
      const msg = `Hello Dr. Avril,%0A%0AI'd like to book an appointment.%0A%0AName: ${encodeURIComponent(form.name)}%0APhone: ${encodeURIComponent(form.phone)}%0AService: ${encodeURIComponent(SERVICES.find(s => s.value === form.service_type)?.label || form.service_type)}%0APreferred Date: ${encodeURIComponent(form.preferred_date || "Any")}%0APreferred Time: ${encodeURIComponent(form.preferred_time || "Any")}%0AConcern: ${encodeURIComponent(form.concern || "—")}`;
      window.open(`https://wa.me/918968303489?text=${msg}`, "_blank");

      setForm({
        name: "",
        phone: "",
        email: "",
        service_type: "neurology",
        preferred_date: "",
        preferred_time: "",
        concern: "",
      });
    } catch (err) {
      toast.error("Could not submit. Please try WhatsApp directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full px-4 py-3 border border-stone-300 bg-white focus:border-brand-navyDark focus:ring-0 outline-none transition-colors text-brand-navyDark placeholder:text-stone-400 text-[14px]";

  return (
    <form
      onSubmit={submit}
      className="bg-white p-9 md:p-11 border border-stone-200"
      data-testid="appointment-form"
    >
      <div className="mb-9 pb-7 border-b border-stone-200">
        <div className="eyebrow mb-3">Book Appointment</div>
        <div className="heading-serif text-3xl font-medium text-brand-navyDark leading-tight">
          Reserve your consultation
        </div>
        <p className="text-[13px] text-brand-muted mt-2">We'll confirm your slot on WhatsApp.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-[11px] uppercase tracking-[0.18em] font-medium text-brand-navyDark mb-2 block">Full Name *</label>
          <input
            type="text"
            value={form.name}
            onChange={update("name")}
            className={inputCls}
            placeholder="Your name"
            required
            data-testid="appt-name"
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-[0.18em] font-medium text-brand-navyDark mb-2 block">Phone *</label>
          <input
            type="tel"
            value={form.phone}
            onChange={update("phone")}
            className={inputCls}
            placeholder="+91 ..."
            required
            data-testid="appt-phone"
          />
        </div>
        <div className="md:col-span-2">
          <label className="text-[11px] uppercase tracking-[0.18em] font-medium text-brand-navyDark mb-2 block">Email</label>
          <input
            type="email"
            value={form.email}
            onChange={update("email")}
            className={inputCls}
            placeholder="you@example.com"
            data-testid="appt-email"
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-[0.18em] font-medium text-brand-navyDark mb-2 block">Service</label>
          <select
            value={form.service_type}
            onChange={update("service_type")}
            className={inputCls}
            data-testid="appt-service"
          >
            {SERVICES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-[0.18em] font-medium text-brand-navyDark mb-2 block">Preferred Date</label>
          <input
            type="date"
            value={form.preferred_date}
            onChange={update("preferred_date")}
            className={inputCls}
            data-testid="appt-date"
          />
        </div>
        <div className="md:col-span-2">
          <label className="text-[11px] uppercase tracking-[0.18em] font-medium text-brand-navyDark mb-2 block">Preferred Time</label>
          <select
            value={form.preferred_time}
            onChange={update("preferred_time")}
            className={inputCls}
            data-testid="appt-time"
          >
            <option value="">Choose a time slot</option>
            <option value="10:00 AM - 12:00 PM">Morning · 10 AM – 12 PM</option>
            <option value="3:00 PM - 5:00 PM">Afternoon · 3 PM – 5 PM</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="text-[11px] uppercase tracking-[0.18em] font-medium text-brand-navyDark mb-2 block">
            Briefly describe your concern
          </label>
          <textarea
            value={form.concern}
            onChange={update("concern")}
            className={`${inputCls} min-h-[110px]`}
            placeholder="Headaches, anxiety, sleep issues, etc."
            data-testid="appt-concern"
          />
        </div>
      </div>

      <div className="mt-7 flex flex-col sm:flex-row gap-3">
        <button
          type="submit"
          disabled={loading}
          className="btn-primary flex-1 disabled:opacity-70"
          data-testid="appt-submit"
        >
          {loading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Calendar className="w-3.5 h-3.5" />
          )}
          {loading ? "Booking..." : "Confirm Appointment"}
        </button>
        <a
          href={CLINIC.whatsapp}
          target="_blank"
          rel="noreferrer"
          className="btn-secondary flex-1"
          data-testid="appt-whatsapp"
        >
          <MessageCircle className="w-3.5 h-3.5" />
          Or WhatsApp Directly
        </a>
      </div>
      <p className="text-[11px] text-brand-muted text-center mt-5 tracking-wide">
        By submitting, you agree to be contacted at the number provided.
      </p>
    </form>
  );
};
